import { useEffect, useMemo, useState } from "react";
import { collection, doc, query, serverTimestamp, setDoc, where } from "firebase/firestore";

import { ErrorNote, Spinner, useToast } from "../components";
import { useCollection, useDoc } from "../hooks";
import { db, auth } from "../lib/firebase";
import type { StatusDoc } from "./Production";

/**
 * MÜŞTERİ BİLDİRİMLERİ (KLAVIYO) — `settings/klaviyo`.
 *
 * ── NİÇİN BU EKRAN VAR ─────────────────────────────────────────────────────
 * Müşteri, sipariş verdikten sonra ürün kargoya çıkana kadar HİÇ haber
 * almıyordu: bugün giden iki otomatik mail (sipariş alındı, kargoya verildi)
 * Shopify'dan çıkıyor, arada kalan üretim süresi müşteri için sessizlik.
 * Atölye aşamayı ilerlettiğinde Klaviyo'ya bir olay yazılır, mailin metnini ve
 * tasarımını Klaviyo tarafındaki akış üretir.
 *
 * ── NİÇİN EŞLEME KODDA DEĞİL BURADA ────────────────────────────────────────
 * "Optimizasyon zımpara olsun, CNC yağlama olsun" gibi bir karar pazarlama
 * kararıdır ve haftadan haftaya değişebilir. Kodda sabit dursaydı her
 * değişiklik dağıtım isterdi.
 *
 * ── AŞAMA LİSTESİ SÖZLÜKTEN OKUNUR ─────────────────────────────────────────
 * Aşama adları buraya YAZILMAZ, `statuses` koleksiyonundan gelir. Sabit yazılsa
 * atölye bir aşamanın adını değiştirdiği gün bu ekran eski adı gösterirdi —
 * bu projede tam bu sınıf ayrışma defalarca yaşandı.
 *
 * Yetki: yalnızca `boss` (çağıran `Settings.tsx` zaten kilitliyor).
 */

/** Sunucudaki `KlaviyoStageMessage` ile BİREBİR aynı alan adları. */
interface StageMessage {
  enabled?: boolean;
  metricName?: string;
  messageKey?: string;
}

interface KlaviyoSettingsDoc {
  enabled?: boolean;
  stages?: Record<string, StageMessage>;
}

/**
 * Sunucudaki `KLAVIYO_DEFAULTS.stages` ile BİREBİR aynı. Doküman hiç yokken
 * ekranın gösterdiği ile sunucunun kullandığı eşleme farklı olsaydı, kullanıcı
 * "Kaydet"e basana kadar iki farklı gerçek olurdu.
 */
const DEFAULT_STAGES: Record<string, Required<StageMessage>> = {
  "prod-cnc": { enabled: true, metricName: "Balder - Kesim", messageKey: "kesim" },
  "prod-prod": {
    enabled: true,
    metricName: "Balder - Zimpara ve Yaglama",
    messageKey: "zimpara-yaglama",
  },
  "prod-packaged": { enabled: true, metricName: "Balder - Paketleme", messageKey: "paketleme" },
};

/** Aşama için neden mail gönderildiğini/gönderilmediğini anlatan kısa not. */
const STAGE_HINT: Record<string, string> = {
  "prod-hold": "Sipariş henüz atölyeye girmedi — anlatacak somut bir şey yok.",
  "prod-planing": "İç planlama adımı; müşteri için görünür bir karşılığı yok.",
  "prod-optimization": "İç planlama adımı; müşteri için görünür bir karşılığı yok.",
  "prod-cnc": "Masif panelin makinede kesildiği adım.",
  "prod-prod": "Zımpara ve doğal yağ uygulaması — anlatmaya en değer adım.",
  "prod-packaged": "Köşe koruma, sargı ve son kontroller.",
  "prod-completed": "Kapalı tutun: kargo maili zaten Shopify'dan gidiyor, çift mail olur.",
  "prod-cancelled": "İptal bir ilerleme değildir; bu aşamada asla mail gönderilmez.",
};

const INPUT =
  "w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500";

function statusOrder(s: StatusDoc & { id: string }): number {
  return s.sortOrder ?? s.ordering ?? 999;
}

export function KlaviyoForm() {
  const toast = useToast();
  const { data, loading, error } = useDoc<KlaviyoSettingsDoc>("settings/klaviyo");

  const statusesQ = useCollection<StatusDoc>(
    () => query(collection(db, "statuses"), where("category", "==", "production")),
    [],
  );

  /**
   * İPTAL LİSTEDE YOK. Sözlükteki `ordering` değeri 8 ile Tamamlandı'nın
   * 7'sinden büyük olduğu için "en ileri aşama" gibi görünür; sunucu onu
   * merdiven dışı sayıyor (`core/statusIds.ts`). Ekranda kapatılabilir bir
   * satır olarak durması, kapatılabilirmiş gibi yanlış bir izlenim verirdi.
   */
  const stages = useMemo(
    () =>
      statusesQ.docs
        .filter((s) => s.obsolete !== true && s.id !== "prod-cancelled")
        .sort((a, b) => statusOrder(a) - statusOrder(b)),
    [statusesQ.docs],
  );

  const [enabled, setEnabled] = useState(false);
  const [rows, setRows] = useState<Record<string, Required<StageMessage>>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (stages.length === 0) return;
    const saved = data?.stages ?? {};
    const next: Record<string, Required<StageMessage>> = {};
    for (const s of stages) {
      const fromDoc = saved[s.id];
      const fallback = DEFAULT_STAGES[s.id];
      next[s.id] = {
        // Kayıtlı doküman varsa O kazanır; yoksa varsayılan eşleme gösterilir.
        enabled: fromDoc ? fromDoc.enabled === true : fallback?.enabled === true,
        metricName: fromDoc?.metricName ?? fallback?.metricName ?? "",
        messageKey: fromDoc?.messageKey ?? fallback?.messageKey ?? "",
      };
    }
    setRows(next);
    setEnabled(data?.enabled === true);
  }, [data, stages]);

  const EMPTY_ROW: Required<StageMessage> = { enabled: false, metricName: "", messageKey: "" };

  function patch(stageId: string, part: Partial<Required<StageMessage>>) {
    // `r[stageId]` sözlükte olmayabilir (statü sonradan eklendi); boş satırla
    // tamamlanmazsa tür zorunlu alanları kaybeder.
    setRows((r) => ({ ...r, [stageId]: { ...EMPTY_ROW, ...r[stageId], ...part } }));
  }

  async function save() {
    setErr(null);

    // ★ Sessiz kabul yok: adsız açık aşama Klaviyo'da "(unnamed)" olarak
    // birikir ve hiçbir akışa bağlanamaz — mail hiç gitmez, hata da vermez.
    for (const s of stages) {
      const row = rows[s.id];
      if (row?.enabled && row.metricName.trim() === "") {
        setErr(`"${s.label ?? s.id}" açık ama mesaj adı boş. Klaviyo'da adsız olay kurulamaz.`);
        return;
      }
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setErr("Oturum bulunamadı. Lütfen yeniden giriş yapın.");
      return;
    }

    /**
     * Haritanın TAMAMI yazılır (`stages` alanı tek parça). Alan alan yazmak
     * `set({merge:true})` ile çalışmaz: noktalı anahtar alan yolu SAYILMAZ,
     * düz metin bir alan adı olarak yazılırdı.
     */
    const stagesOut: Record<string, Required<StageMessage>> = {};
    for (const s of stages) {
      const row = rows[s.id];
      if (!row) continue;
      const metricName = row.metricName.trim();
      if (metricName === "") continue; // adsız satır hiç yazılmaz
      stagesOut[s.id] = {
        enabled: row.enabled === true,
        metricName,
        messageKey: row.messageKey.trim() || s.id,
      };
    }

    setBusy(true);
    try {
      await setDoc(
        doc(db, "settings", "klaviyo"),
        {
          enabled,
          stages: stagesOut,
          // Doküman henüz yoksa Rules `createStamp()` arar (createdAt/createdBy
          // + deleted:false); bunlar olmadan ilk kayıt "izin yok" ile dönerdi.
          ...(data ? {} : { createdAt: serverTimestamp(), createdBy: uid, deleted: false }),
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast.success("Müşteri bildirim ayarları kaydedildi.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kaydedilemedi.");
    } finally {
      setBusy(false);
    }
  }

  if (loading || statusesQ.loading) return <Spinner />;

  return (
    <section className="mt-8">
      <h2 className="text-base font-semibold text-gray-900">Müşteri Bildirimleri (Klaviyo)</h2>
      <p className="mt-1 max-w-3xl text-sm text-gray-600">
        Üretim aşaması <strong>ileri</strong> gittiğinde müşteriye Klaviyo üzerinden mail
        gönderilir. Aşama geri alınırsa <strong>mail gitmez</strong>; her aşama için sipariş
        başına en fazla bir kez gönderilir. Sipariş alındı ve kargoya verildi mailleri
        Shopify'da kalır — buradan gönderilmez.
      </p>

      <ErrorNote error={err ?? error ?? statusesQ.error} />

      <label className="mt-4 flex items-start gap-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="mt-0.5 h-4 w-4"
        />
        <span className="text-sm">
          <strong>Müşteri bildirimleri açık</strong>
          <span className="block text-gray-600">
            Kapatıldığında hiçbir aşamada mail gitmez. Aşama ilerlemeleri yine de kaydedilir,
            yani sonradan açtığınızda geçmiş aşamaların maili toplu olarak gitmez.
          </span>
        </span>
      </label>

      <div className="mt-5 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <th className="py-2 pr-3 font-medium">Mail</th>
              <th className="py-2 pr-3 font-medium">Aşama (atölye görür)</th>
              <th className="py-2 pr-3 font-medium">Mesaj adı (Klaviyo metriği)</th>
              <th className="py-2 font-medium">Şablon anahtarı</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((s) => {
              const row = rows[s.id] ?? EMPTY_ROW;
              return (
                <tr key={s.id} className="border-b border-slate-100 align-top">
                  <td className="py-2.5 pr-3">
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      onChange={(e) => patch(s.id, { enabled: e.target.checked })}
                      className="h-4 w-4"
                      aria-label={`${s.label ?? s.id} aşamasında mail gönder`}
                    />
                  </td>
                  <td className="py-2.5 pr-3">
                    <div className="font-medium text-gray-900">{s.label ?? s.id}</div>
                    <div className="text-xs text-gray-500">{STAGE_HINT[s.id] ?? ""}</div>
                  </td>
                  <td className="py-2.5 pr-3">
                    <input
                      value={row.metricName}
                      onChange={(e) => patch(s.id, { metricName: e.target.value })}
                      className={INPUT}
                      placeholder="ör. Balder - Kesim"
                    />
                  </td>
                  <td className="py-2.5">
                    <input
                      value={row.messageKey}
                      onChange={(e) => patch(s.id, { messageKey: e.target.value })}
                      className={INPUT}
                      placeholder="ör. kesim"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 max-w-3xl rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <strong>Mesaj adını değiştirmek Klaviyo'da YENİ bir metrik açar.</strong> Eski ada bağlı
        akış çalışmaya devam eder ama artık olay almaz — mail sessizce durur. Adı değiştirirseniz
        Klaviyo tarafındaki akışı da yeni metriğe bağlayın.
      </p>

      <p className="mt-2 max-w-3xl text-sm text-gray-600">
        Trendyol siparişlerine mail gönderilmez: pazaryeri vekil adresi kullanır
        (<code>@trendyolmail.com</code>) ve mail müşteriye ulaşmaz. E-posta adresi olmayan
        manuel/iç siparişler de atlanır.
      </p>

      <div className="mt-4">
        <button type="button" onClick={save} disabled={busy} className="btn-primary">
          {busy ? "Kaydediliyor…" : "Bildirim Ayarlarını Kaydet"}
        </button>
      </div>
    </section>
  );
}

export default KlaviyoForm;
